import { useState, useEffect } from "react";
import { 
  Users, 
  Plus, 
  Trash2,
  Edit,
  DollarSign,
  Calendar,
  Mail,
  Phone
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const payTypes = [
  { value: "hourly", label: "Hourly" },
  { value: "salary", label: "Salary" },
  { value: "contract", label: "Contract" }
];

const Team = ({ user }) => {
  const [team, setTeam] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMemberDialog, setShowMemberDialog] = useState(false);
  const [showPayrollDialog, setShowPayrollDialog] = useState(false);

  const [memberForm, setMemberForm] = useState({
    name: "",
    role: "",
    email: "",
    phone: "",
    start_date: "",
    pay_type: "hourly",
    pay_rate: 0,
    notes: ""
  });

  const [payrollForm, setPayrollForm] = useState({
    team_member_id: "",
    team_member_name: "",
    period_start: "",
    period_end: "",
    amount: 0,
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamRes, payrollRes] = await Promise.all([
        fetch(`${API}/team`, { credentials: "include" }),
        fetch(`${API}/payroll`, { credentials: "include" })
      ]);

      if (teamRes.ok) {
        const data = await teamRes.json();
        setTeam(data?.team ?? data ?? []);
      } else {
        setTeam([]);
      }
      if (payrollRes.ok) {
        const data = await payrollRes.json();
        setPayroll(data?.payroll ?? data ?? []);
      } else {
        setPayroll([]);
      }
    } catch (error) {
      toast.error("Failed to load data");
      setTeam([]);
      setPayroll([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    try {
      const response = await fetch(`${API}/team`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(memberForm)
      });

      if (response.ok) {
        toast.success("Team member added");
        setShowMemberDialog(false);
        setMemberForm({ name: "", role: "", email: "", phone: "", start_date: "", pay_type: "hourly", pay_rate: 0, notes: "" });
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to add team member");
    }
  };

  const handleAddPayroll = async () => {
    try {
      const response = await fetch(`${API}/payroll`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payrollForm)
      });

      if (response.ok) {
        toast.success("Payroll entry added");
        setShowPayrollDialog(false);
        setPayrollForm({ team_member_id: "", team_member_name: "", period_start: "", period_end: "", amount: 0, notes: "" });
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to add payroll entry");
    }
  };

  const handleDeleteMember = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    try {
      await fetch(`${API}/team/${id}`, { method: "DELETE", credentials: "include" });
      toast.success("Team member removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleMarkPaid = async (entry) => {
    try {
      await fetch(`${API}/payroll/${entry.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: "paid", paid_date: new Date().toISOString().split('T')[0] })
      });
      toast.success("Marked as paid");
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="team-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#FAFAFA]">Team & Payroll</h1>
          <p className="text-[#A1A1AA] mt-1">Manage your team members and payroll</p>
        </div>
      </div>

      <Tabs defaultValue="team" className="space-y-4">
        <TabsList className="bg-[#121214] border border-[#27272A]">
          <TabsTrigger value="team" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]">
            Team Directory
          </TabsTrigger>
          <TabsTrigger value="payroll" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]">
            Payroll
          </TabsTrigger>
        </TabsList>

        <TabsContent value="team">
          <div className="flex justify-end mb-4">
            <Dialog open={showMemberDialog} onOpenChange={setShowMemberDialog}>
              <DialogTrigger asChild>
                <Button className="btn-gold" data-testid="add-member-btn">
                  <Plus size={18} className="mr-2" /> Add Team Member
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121214] border-[#27272A]">
                <DialogHeader>
                  <DialogTitle className="text-[#FAFAFA]">Add Team Member</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#A1A1AA]">Name</Label>
                      <Input
                        value={memberForm.name}
                        onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Role</Label>
                      <Input
                        value={memberForm.role}
                        onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })}
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#A1A1AA]">Email</Label>
                      <Input
                        type="email"
                        value={memberForm.email}
                        onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Phone</Label>
                      <Input
                        value={memberForm.phone}
                        onChange={(e) => setMemberForm({ ...memberForm, phone: e.target.value })}
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-[#A1A1AA]">Start Date</Label>
                      <Input
                        type="date"
                        value={memberForm.start_date}
                        onChange={(e) => setMemberForm({ ...memberForm, start_date: e.target.value })}
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Pay Type</Label>
                      <Select value={memberForm.pay_type} onValueChange={(v) => setMemberForm({ ...memberForm, pay_type: v })}>
                        <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                          {payTypes.map((t) => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Pay Rate ($)</Label>
                      <Input
                        type="number"
                        value={memberForm.pay_rate}
                        onChange={(e) => setMemberForm({ ...memberForm, pay_rate: parseFloat(e.target.value) || 0 })}
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Notes</Label>
                    <Textarea
                      value={memberForm.notes}
                      onChange={(e) => setMemberForm({ ...memberForm, notes: e.target.value })}
                      className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    />
                  </div>
                  <Button onClick={handleAddMember} className="w-full btn-gold">
                    Add Team Member
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {team.map((member) => (
              <Card key={member.id} className="bg-[#121214] border-[#27272A]" data-testid={`team-card-${member.id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#0A0A0B] font-bold text-lg">
                        {member.name?.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#FAFAFA]">{member.name}</h3>
                        <p className="text-sm text-[#D4AF37]">{member.role}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteMember(member.id)} className="text-[#EF4444]">
                      <Trash2 size={16} />
                    </Button>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    {member.email && (
                      <p className="text-[#A1A1AA] flex items-center gap-2">
                        <Mail size={14} /> {member.email}
                      </p>
                    )}
                    {member.phone && (
                      <p className="text-[#A1A1AA] flex items-center gap-2">
                        <Phone size={14} /> {member.phone}
                      </p>
                    )}
                    <p className="text-[#A1A1AA] flex items-center gap-2">
                      <Calendar size={14} /> Since {member.start_date}
                    </p>
                    <p className="text-[#D4AF37] flex items-center gap-2">
                      <DollarSign size={14} /> {formatCurrency(member.pay_rate)} / {member.pay_type}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
            {team.length === 0 && (
              <div className="col-span-full text-center py-12">
                <Users size={48} className="text-[#71717A] mx-auto mb-3" />
                <p className="text-[#A1A1AA]">No team members yet</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="payroll">
          <div className="flex justify-end mb-4">
            <Dialog open={showPayrollDialog} onOpenChange={setShowPayrollDialog}>
              <DialogTrigger asChild>
                <Button className="btn-gold" data-testid="add-payroll-btn">
                  <Plus size={18} className="mr-2" /> Add Payroll Entry
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121214] border-[#27272A]">
                <DialogHeader>
                  <DialogTitle className="text-[#FAFAFA]">Add Payroll Entry</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="text-[#A1A1AA]">Team Member</Label>
                    <Select 
                      value={payrollForm.team_member_id} 
                      onValueChange={(v) => {
                        const member = team.find(m => m.id === v);
                        setPayrollForm({ ...payrollForm, team_member_id: v, team_member_name: member?.name || "" });
                      }}
                    >
                      <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                        <SelectValue placeholder="Select team member" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                        {team.map((m) => (
                          <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#A1A1AA]">Period Start</Label>
                      <Input
                        type="date"
                        value={payrollForm.period_start}
                        onChange={(e) => setPayrollForm({ ...payrollForm, period_start: e.target.value })}
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Period End</Label>
                      <Input
                        type="date"
                        value={payrollForm.period_end}
                        onChange={(e) => setPayrollForm({ ...payrollForm, period_end: e.target.value })}
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Amount ($)</Label>
                    <Input
                      type="number"
                      value={payrollForm.amount}
                      onChange={(e) => setPayrollForm({ ...payrollForm, amount: parseFloat(e.target.value) || 0 })}
                      className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Notes</Label>
                    <Textarea
                      value={payrollForm.notes}
                      onChange={(e) => setPayrollForm({ ...payrollForm, notes: e.target.value })}
                      className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    />
                  </div>
                  <Button onClick={handleAddPayroll} className="w-full btn-gold">
                    Add Payroll Entry
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="bg-[#121214] border-[#27272A]">
            <CardContent className="pt-6">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Team Member</th>
                    <th>Period</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payroll.map((entry) => (
                    <tr key={entry.id}>
                      <td className="text-[#FAFAFA]">{entry.team_member_name}</td>
                      <td className="text-[#A1A1AA]">{entry.period_start} - {entry.period_end}</td>
                      <td className="text-[#D4AF37] font-medium">{formatCurrency(entry.amount)}</td>
                      <td>
                        <span className={`badge ${entry.status === 'paid' ? 'badge-success' : 'badge-warning'}`}>
                          {entry.status}
                        </span>
                      </td>
                      <td>
                        {entry.status !== 'paid' && (
                          <Button variant="ghost" size="sm" onClick={() => handleMarkPaid(entry)} className="text-[#22C55E]">
                            Mark Paid
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {payroll.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-[#71717A] py-8">No payroll entries yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Team;
